/**
 * Wall-clock time in an IANA zone ↔ instant — the SINGLE isomorphic implementation.
 *
 * The frontend prints every date and reads every typed date on the user's profile clock with these; the backend
 * cuts the ledger's day filters and the fiscal year on the same clock. One implementation, so the day the grid
 * prints and the year the tax report assigns can never disagree.
 *
 * WHY THIS EXISTS. The app stores every moment as a UTC instant, and the user picks a display zone in their
 * profile (`timezone`, `UTC` by default). The screens used to read `getHours()` & co., i.e. the zone of the
 * BROWSER, so the profile zone was a preference nobody obeyed: Tokyo and New York (14 h apart) printed the
 * same 23:30. The tax report buckets by the stored UTC instant, so a laptop in Madrid could show an entry in
 * one year while it was taxed in another. Every surface now renders — and every typed date is read — through
 * these two functions, in the profile zone.
 *
 * Pure and dependency-free: the zone is an argument, never global state, so tests can vary it freely.
 * An unknown zone name (legacy row, hand-edited storage) falls back to the RUNTIME's zone (the browser's, or the
 * server process's — UTC) instead of throwing.
 */

import { isRealCalendarDay } from './calendar-day.js'

export interface WallClock {
  year: number
  /** 1–12 */
  month: number
  day: number
  hours: number
  minutes: number
  seconds: number
}

const DAY_MS = 24 * 60 * 60 * 1000

const formatters = new Map<string, Intl.DateTimeFormat | null>()

/** One cached formatter per zone (building it is the expensive part); `null` = the zone is not valid. */
function formatterFor(timeZone: string): Intl.DateTimeFormat | null {
  const cached = formatters.get(timeZone)
  if (cached !== undefined) return cached
  let formatter: Intl.DateTimeFormat | null
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    formatter = null
  }
  formatters.set(timeZone, formatter)
  return formatter
}

/** The wall clock of `instant` in `timeZone`; `null` for an invalid date. */
export function wallClockInZone(instant: Date, timeZone: string): WallClock | null {
  if (Number.isNaN(instant.getTime())) return null

  const formatter = formatterFor(timeZone)
  if (!formatter) {
    return {
      year: instant.getFullYear(),
      month: instant.getMonth() + 1,
      day: instant.getDate(),
      hours: instant.getHours(),
      minutes: instant.getMinutes(),
      seconds: instant.getSeconds(),
    }
  }

  const parts: Record<string, number> = {}
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value)
  }
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    // Some engines print midnight as «24» even with h23.
    hours: parts.hour === 24 ? 0 : parts.hour,
    minutes: parts.minute,
    seconds: parts.second,
  }
}

/**
 * `timeZone` when it is a usable IANA name, else `undefined` — the value to hand to `Intl` / `toLocale*String`
 * so an unknown zone falls back to the browser's instead of throwing a `RangeError`.
 */
export function usableTimeZone(timeZone: string): string | undefined {
  return formatterFor(timeZone) ? timeZone : undefined
}

/** Milliseconds `timeZone` is ahead of UTC at `utcMs` (whole seconds, so it is exact for any instant). */
function offsetMs(utcMs: number, timeZone: string): number {
  const whole = Math.floor(utcMs / 1000) * 1000
  const clock = wallClockInZone(new Date(whole), timeZone)
  if (!clock) return 0
  return (
    Date.UTC(clock.year, clock.month - 1, clock.day, clock.hours, clock.minutes, clock.seconds) -
    whole
  )
}

/**
 * The instant whose wall clock in `timeZone` reads `clock`. Two passes settle the offset around a DST change;
 * a time that does not exist (spring-forward gap) lands just after the gap, an ambiguous one (autumn overlap)
 * resolves to the first occurrence. `null` when the calendar day or the clock time is not real.
 */
export function instantFromWallClock(clock: WallClock, timeZone: string): Date | null {
  const { year, month, day, hours, minutes, seconds } = clock
  if (!isRealCalendarDay(year, month, day)) return null
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
    return null
  }

  if (!formatterFor(timeZone)) {
    const local = new Date(year, month - 1, day, hours, minutes, seconds)
    return Number.isNaN(local.getTime()) ? null : local
  }

  const asIfUtc = Date.UTC(year, month - 1, day, hours, minutes, seconds)
  // The offset can only change once around a DST switch, so the day before and the day after bracket both
  // sides of it. A candidate is real when the zone reads exactly `clock` back at that instant.
  const before = offsetMs(asIfUtc - DAY_MS, timeZone)
  const after = offsetMs(asIfUtc + DAY_MS, timeZone)
  const real = [...new Set([before, after])]
    .map((offset) => asIfUtc - offset)
    .filter((candidate) => offsetMs(candidate, timeZone) === asIfUtc - candidate)
  // Overlap → two real instants, the earlier one wins. Gap → none: use the offset in force before it.
  return new Date(real.length > 0 ? Math.min(...real) : asIfUtc - before)
}

/** Today's calendar date in `timeZone`. */
export function todayInZone(
  timeZone: string,
  now: Date = new Date(),
): Pick<WallClock, 'year' | 'month' | 'day'> {
  const clock = wallClockInZone(now, timeZone)
  return clock
    ? { year: clock.year, month: clock.month, day: clock.day }
    : { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
}

export type CalendarDay = Pick<WallClock, 'year' | 'month' | 'day'>

/** `day` moved by `days` calendar days (month and year roll over; no zone involved). */
export function addCalendarDays(day: CalendarDay, days: number): CalendarDay {
  const moved = new Date(Date.UTC(day.year, day.month - 1, day.day + days))
  return { year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1, day: moved.getUTCDate() }
}

/** The first instant of `day` on `timeZone`'s clock (a day starting in a DST gap starts when the gap ends). */
export function startOfDayInZone(day: CalendarDay, timeZone: string): Date {
  return (
    instantFromWallClock({ ...day, hours: 0, minutes: 0, seconds: 0 }, timeZone) ??
    new Date(Date.UTC(day.year, day.month - 1, day.day))
  )
}

/** The LAST instant of `day` on `timeZone`'s clock: one millisecond before the next day starts. */
export function endOfDayInZone(day: CalendarDay, timeZone: string): Date {
  return new Date(startOfDayInZone(addCalendarDays(day, 1), timeZone).getTime() - 1)
}

/** `YYYY-MM-DD` (what `<input type="date">` gives) as a calendar day; `null` when it is not a real day. */
export function parseIsoCalendarDay(value: string): CalendarDay | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) return null
  const day = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
  return isRealCalendarDay(day.year, day.month, day.day) ? day : null
}

/**
 * The inclusive instant range a date-only bound means on the user's clock: `from` → start of that day,
 * `to` → its last millisecond. A value that already carries a time (`…T…`) is an instant and is kept as-is,
 * so the conversion is idempotent across URL round-trips. `undefined` for an empty or unreadable value.
 */
export function dayBoundInZone(
  value: string | undefined,
  edge: 'from' | 'to',
  timeZone: string,
): Date | undefined {
  if (value === undefined || value.trim() === '') return undefined
  if (value.includes('T')) {
    const instant = new Date(value)
    return Number.isNaN(instant.getTime()) ? undefined : instant
  }
  const day = parseIsoCalendarDay(value)
  if (!day) return undefined
  return edge === 'from' ? startOfDayInZone(day, timeZone) : endOfDayInZone(day, timeZone)
}

/** The calendar year `instant` falls in on `timeZone`'s clock. */
export function yearInZone(instant: Date, timeZone: string): number {
  return wallClockInZone(instant, timeZone)?.year ?? instant.getUTCFullYear()
}

/**
 * A whole calendar year on `timeZone`'s clock as a HALF-OPEN instant window `[start, endExclusive)`: it partitions
 * the timeline with no gap and no overlap, so a ms-precision disposal at 23:59:59.500 on 31 December belongs to
 * exactly one year.
 */
export function yearRangeInZone(
  year: number,
  timeZone: string,
): { start: Date; endExclusive: Date } {
  return {
    start: startOfDayInZone({ year, month: 1, day: 1 }, timeZone),
    endExclusive: startOfDayInZone({ year: year + 1, month: 1, day: 1 }, timeZone),
  }
}

/**
 * The calendar day of `instant` on `timeZone`'s clock as a day NUMBER (days since 1970-01-01). Differences of day
 * numbers count calendar days — «held for more than a year», «within 30 days» — the way the user reads the dates.
 */
export function calendarDayNumber(instant: Date, timeZone: string): number {
  const clock = wallClockInZone(instant, timeZone)
  const { year, month, day } = clock ?? {
    year: instant.getUTCFullYear(),
    month: instant.getUTCMonth() + 1,
    day: instant.getUTCDate(),
  }
  return Math.round(Date.UTC(year, month - 1, day) / DAY_MS)
}

/**
 * The zone's CURRENT offset from UTC at `instant`, as a short label: `UTC+2`, `UTC-4`, `UTC+5:30`, `UTC`.
 *
 * Offsets move with daylight saving, so a label written once is false half the year: the profile picker said
 * «Madrid (UTC+1)» on 30/09/2026, when Madrid was on UTC+2. Computing it at `instant` keeps the label true on
 * the day it is read. `null` for an invalid date or zone.
 */
export function utcOffsetLabel(timeZone: string, instant: Date): string | null {
  if (usableTimeZone(timeZone) === undefined || Number.isNaN(instant.getTime())) return null
  const offsetMinutes = Math.round(offsetMs(instant.getTime(), timeZone) / 60000)
  if (offsetMinutes === 0) return 'UTC'
  const sign = offsetMinutes > 0 ? '+' : '-'
  const hours = Math.floor(Math.abs(offsetMinutes) / 60)
  const minutes = Math.abs(offsetMinutes) % 60
  return `UTC${sign}${hours}${minutes === 0 ? '' : `:${String(minutes).padStart(2, '0')}`}`
}
